import { changeNames } from "../../lib/change-names";
import { useIdolRoster } from "../../lib/useIdolRoster";
import { pickText } from "../../lib/locales";
import type { Change } from "../../lib/autosave";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Notice";
import { Tag } from "../ui/Tag";
import { useI18n, useT } from "../../lib/i18n";
import { fmt } from "../../lib/model";
import { diffLevels, diffSnapshot, type DiffRow, type Snapshot } from "../../lib/version";
import { useChangeLabels } from "./changeLabels";

const th = "border-b border-line-lighter bg-white px-3 py-1.5 text-left text-xs font-medium text-ink-secondary";
const td = "border-b border-line-lighter px-3 py-1.5 text-ink-regular";

function rowLabel(t: ReturnType<typeof useT>, r: DiffRow): string {
  switch (r.kind) {
    case "detail": return "";
    case "field": return t(`ver.f.${r.field}` as "ver.f.B");
    case "total": return t("ver.diff.total") + t("ver.diff.derived");
    case "cell": return t("ver.diff.cell", { b: r.b, l: r.l });
    case "added": return t("ver.diff.added");
    case "removed": return t("ver.diff.removed");
  }
}

const show = (v: string | number | null | undefined, numeric: boolean) =>
  v == null ? "—" : numeric && (typeof v === "number" || /^-?\d+$/.test(v)) ? fmt(v) : String(v);

/** 两个版本的完整快照差异：先列全局/业务块，再列逐关与逐格变化。`tabbed` 时每个模块一个页签（发布确认用） */
export function DiffView({
  left, right, leftLabel, rightLabel, onJump, tabbed = false, authoritativeChanges, samePublishedContent = false, checksumConflict = false,
}: { left: Snapshot; right: Snapshot; leftLabel: string; rightLabel: string; onJump?: (id: number) => void; tabbed?: boolean; authoritativeChanges?: Change[]; samePublishedContent?: boolean; checksumConflict?: boolean }) {
  const t = useT();
  const { where, field, side, section } = useChangeLabels();
  const leftLabels = useChangeLabels(left, true), rightLabels = useChangeLabels(right, true);
  const d = useMemo(() => authoritativeChanges ? { groups: [], cellCount: 0 } : diffLevels(left.levels, right.levels), [left.levels, right.levels, authoritativeChanges]);
  const { lang } = useI18n();
  const { idols } = useIdolRoster(!!authoritativeChanges?.some((c) => c.block === "stageImages" || c.block === "stages"));
  const configRows = useMemo(() => {
    const name = (id: string) => { const idol = idols.find((i) => i.idolId === id); return idol ? pickText(idol.stageNameI18n, lang) : id; };
    const rows = authoritativeChanges ?? diffSnapshot(left, right);
    return changeNames(changeNames(rows, right, t, name), left, t, name);
  }, [left, right, authoritativeChanges, idols, lang, t]);
  /** 页签：关卡在前（改动最常见），其余模块按快照里的先后；一个模块一个签，签上带改了几处 */
  const tabs = useMemo(() => {
    const out: Array<{ id: string; label: string; n: number }> = [];
    if (d.groups.length) out.push({ id: LEVELS_TAB, label: t("nav.level"), n: d.groups.reduce((a, g) => a + g.rows.length, 0) });
    const byBlock = new Map<string, number>();
    for (const r of configRows) byBlock.set(r.block, (byBlock.get(r.block) ?? 0) + 1);
    // 两个块叫法相同（亲密度的等级 / 来源 / 参数）就并成一个签，免得出现两个同名页签
    const byLabel = new Map<string, { id: string; label: string; n: number; blocks: string[] }>();
    for (const [b, n] of byBlock) {
      const label = section(b);
      const hit = byLabel.get(label);
      if (hit) { hit.n += n; hit.blocks.push(b); } else byLabel.set(label, { id: b, label, n, blocks: [b] });
    }
    return [...out.map((x) => ({ ...x, blocks: [] as string[] })), ...byLabel.values()];
  }, [d.groups, configRows, section, t]);
  const [tab, setTab] = useState<string | null>(null);
  const [rowPage, setRowPage] = useState(0);
  useEffect(() => { setRowPage(0); }, [left, right, tab]);
  if (!d.groups.length && !configRows.length) return <Notice tone={checksumConflict ? "warning" : "plain"}>{checksumConflict ? t("auditChange.checksumConflict") : samePublishedContent ? t("ver.diff.none") : t("auditChange.noVisibleDiff")}</Notice>;
  const sectionCount = new Set(configRows.map((r) => r.block)).size;
  const cur = tabbed ? tabs.find((x) => x.id === tab) ?? tabs[0] : null;
  const shownRows = cur ? configRows.filter((r) => cur.blocks.includes(r.block)) : configRows;
  const showLevels = !cur || cur.id === LEVELS_TAB;

  const configTable = shownRows.length > 0 && (
    <div className="rounded-base border border-line-lighter">
      {!cur && (
        <div className="flex items-center gap-2.5 bg-fill-light px-3 py-2 text-sm font-semibold">
          <span>{t("ver.diff.configTitle")}</span>
          <Tag tone="warning">{t("ver.diff.changes", { n: configRows.length })}</Tag>
        </div>
      )}
      <table className="w-full table-fixed border-collapse text-sm">
        <thead><tr>
          <th className={`${th} w-[34%]`}>{t("ver.diff.item")}</th>
          <th className={`${th} w-[33%]`}>{leftLabel}</th>
          <th className={`${th} w-[33%]`}>{rightLabel}</th>
        </tr></thead>
        <tbody>
          {shownRows.slice(rowPage * 100, (rowPage + 1) * 100).map((r) => (
            <tr key={`${r.block}|${r.path}`} className={r.op === "add" ? "bg-success-50" : r.op === "remove" ? "bg-danger-50" : "bg-warning-50"}>
              {/* 分签时模块名已经在签上，这里只写行与字段 */}
              <td className={`${td} break-all text-xs`}>{cur ? where(r).replace(`${section(r.block)} · `, "") : where(r)} · {field(r)}</td>
              {/* 整行新增 / 删除：把那一行的内容列出来（哪一关、发什么），只写行名看不出加了什么 */}
              <td className={`${td} break-all text-ink-secondary ${r.op ? "" : "line-through"}`}>{r.op === "add" ? "" : leftLabels.cellValue(side(r, "from"), r.row, r.op ? undefined : r.to)}</td>
              <td className={`${td} break-all font-semibold`}>{r.op === "remove" ? "" : rightLabels.cellValue(side(r, "to"), r.row, r.op ? undefined : r.from)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {shownRows.length > 100 && <div className="flex items-center gap-3 px-3 py-2 text-sm text-ink-secondary">
        <span>{t("auditChange.shownRange", { from: rowPage * 100 + 1, to: Math.min((rowPage + 1) * 100, shownRows.length), total: shownRows.length })}</span>
        <select aria-label={t("auditChange.changePage")} className="rounded-base border border-line px-2 py-1" value={rowPage} onChange={(e) => setRowPage(Number(e.target.value))}>
          {Array.from({ length: Math.ceil(shownRows.length / 100) }, (_, n) => <option key={n} value={n}>{n + 1}</option>)}
        </select>
        <Button size="sm" disabled={rowPage === 0} onClick={() => setRowPage((n) => n - 1)}>{t("auditChange.previousChanges")}</Button>
        <Button size="sm" disabled={(rowPage + 1) * 100 >= shownRows.length} onClick={() => setRowPage((n) => n + 1)}>{t("auditChange.nextChanges")}</Button>
      </div>}
    </div>
  );
  const levelGroups = showLevels && d.groups.map((g) => (
    <div key={g.level.id} className="rounded-base border border-line-lighter">
      <div className="flex items-center gap-2.5 bg-fill-light px-3 py-2 text-sm font-semibold">
        <span>{t("ver.diff.level", { id: g.level.id, name: g.level.name })}</span>
        <Tag tone="warning">{t("ver.diff.changes", { n: g.rows.length })}</Tag>
        {onJump && (
          <button type="button" onClick={() => onJump(g.level.id)} className="ml-auto cursor-pointer text-xs font-normal text-primary">
            {t("ver.diff.jump")}
          </button>
        )}
      </div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className={`${th} w-[34%]`}>{t("ver.diff.item")}</th>
            <th className={`${th} w-[33%]`}>{leftLabel}</th>
            <th className={`${th} w-[33%]`}>{rightLabel}</th>
          </tr>
        </thead>
        <tbody>
          {g.rows.map((r, i) => {
            const numeric = r.kind === "cell" || r.kind === "total" || (r.kind === "field" && r.num);
            const from = r.kind === "added" ? null : r.kind === "removed" ? r.name : r.kind === "detail" ? undefined : r.from;
            const to = r.kind === "removed" ? null : r.kind === "added" ? r.name : r.kind === "detail" ? undefined : r.to;
            return (
              <tr key={i} className="bg-warning-50">
                <td className={td}>{r.kind === "detail" ? field(r.change) : rowLabel(t, r)}</td>
                <td className={`${td} text-right tabular-nums text-ink-secondary line-through`}>{r.kind === "detail" ? leftLabels.cellValue(side(r.change, "from"), r.change.row, side(r.change, "to")) : show(from, numeric)}</td>
                <td className={`${td} text-right font-semibold tabular-nums`}>{r.kind === "detail" ? rightLabels.cellValue(side(r.change, "to"), r.change.row, side(r.change, "from")) : show(to, numeric)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ));
  return (
    <div>
      <div className="mb-3 flex items-center gap-3 rounded-base border border-primary-100 bg-primary-50 px-3 py-2.5">
        <strong className="text-primary-700">{authoritativeChanges ? t("auditChange.fullCount", { sections: sectionCount, fields: configRows.length }) : t("ver.diff.fullSummary", { sections: sectionCount, fields: configRows.length, levels: d.groups.length, cells: d.cellCount })}</strong>
        <span className="ml-auto text-sm text-ink-secondary">{leftLabel} → {rightLabel}</span>
      </div>
      {cur && (
        <div role="tablist" className="mb-3 flex flex-wrap gap-x-5 gap-y-1 border-b border-line-lighter">
          {tabs.map((x) => (
            <button key={x.id} type="button" role="tab" aria-selected={x.id === cur.id} onClick={() => setTab(x.id)}
              className={`-mb-px border-b-2 pb-2 text-sm font-medium ${x.id === cur.id ? "border-primary text-primary" : "border-transparent text-ink-secondary hover:text-primary"}`}>
              {x.label}<span className="ml-1 text-xs font-normal text-ink-placeholder">{x.n}</span>
            </button>
          ))}
        </div>
      )}
      <div role={cur ? "tabpanel" : undefined} className="flex flex-col gap-3">
        {configTable}
        {levelGroups}
      </div>
    </div>
  );
}

const LEVELS_TAB = "__levels";
